import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type Contact, createContact, deleteContact, fetchContacts, updateContact } from '../api/resources';

export function ContactsPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ['contacts'], queryFn: fetchContacts });
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [formState, setFormState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    tags: '',
  });
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editState, setEditState] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    tags: '',
  });

  const createMutation = useMutation({
    mutationFn: createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setFormOpen(false);
      setFormState({ firstName: '', lastName: '', email: '', phone: '', company: '', tags: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contacts'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: {
        firstName: string;
        lastName?: string;
        email?: string;
        phone?: string;
        company?: string;
        tags?: string[];
      };
    }) => updateContact(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setEditingContact(null);
    },
  });

  const openEditor = (contact: Contact) => {
    setEditingContact(contact);
    setEditState({
      firstName: contact.first_name,
      lastName: contact.last_name ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      company: contact.company ?? '',
      tags: contact.tags ? contact.tags.split(',').map((tag) => tag.trim()).join(', ') : '',
    });
  };

  const closeEditor = () => {
    setEditingContact(null);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Contacts</h1>
          <p>Centralize every lead, client, and relationship.</p>
        </div>
        <div className="page-header-actions">
          <Link className="btn btn--secondary" to="/import?type=contacts">
            Import CSV
          </Link>
          <button className="btn btn--primary" onClick={() => setFormOpen(true)}>
            New contact
          </button>
        </div>
      </div>

      {formOpen && (
        <div className="card">
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate({
                firstName: formState.firstName,
                lastName: formState.lastName || undefined,
                email: formState.email || undefined,
                phone: formState.phone || undefined,
                company: formState.company || undefined,
                tags: formState.tags ? formState.tags.split(',').map((tag) => tag.trim()) : undefined,
              });
            }}
          >
            <div className="form-grid">
              <label>
                First name
                <input value={formState.firstName} onChange={(e) => setFormState((s) => ({ ...s, firstName: e.target.value }))} required />
              </label>
              <label>
                Last name
                <input value={formState.lastName} onChange={(e) => setFormState((s) => ({ ...s, lastName: e.target.value }))} />
              </label>
            </div>
            <div className="form-grid">
              <label>
                Email
                <input type="email" value={formState.email} onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))} />
              </label>
              <label>
                Phone
                <input value={formState.phone} onChange={(e) => setFormState((s) => ({ ...s, phone: e.target.value }))} />
              </label>
            </div>
            <label>
              Company
              <input value={formState.company} onChange={(e) => setFormState((s) => ({ ...s, company: e.target.value }))} />
            </label>
            <label>
              Tags (comma separated)
              <input value={formState.tags} onChange={(e) => setFormState((s) => ({ ...s, tags: e.target.value }))} />
            </label>
            <div className="form-actions">
              <button className="btn" type="button" onClick={() => setFormOpen(false)}>
                Cancel
              </button>
              <button className="btn btn--primary" type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create contact'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {isLoading && <p>Loading contacts…</p>}
        {error && <p>Error loading contacts</p>}
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Deals</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data?.map((contact) => (
              <tr key={contact.id} className="table-row" onClick={() => openEditor(contact)}>
                <td>{[contact.first_name, contact.last_name].filter(Boolean).join(' ')}</td>
                <td>{contact.email ?? '—'}</td>
                <td>{contact.company ?? '—'}</td>
                <td>{contact.deal_count}</td>
                <td>
                  <button
                    className="btn btn--ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(contact.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!data?.length && (
              <tr>
                <td colSpan={5}>No contacts yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingContact && (
        <div className="modal-overlay" onClick={closeEditor}>
          <div
            className="modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h2>Edit contact</h2>
            <p className="modal-description">Update details and they will sync instantly across the workspace.</p>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                updateMutation.mutate({
                  id: editingContact.id,
                  payload: {
                    firstName: editState.firstName,
                    lastName: editState.lastName || undefined,
                    email: editState.email || undefined,
                    phone: editState.phone || undefined,
                    company: editState.company || undefined,
                    tags: editState.tags
                      ? editState.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
                      : undefined,
                  },
                });
              }}
            >
              <div className="form-grid">
                <label>
                  First name
                  <input value={editState.firstName} onChange={(e) => setEditState((s) => ({ ...s, firstName: e.target.value }))} required />
                </label>
                <label>
                  Last name
                  <input value={editState.lastName} onChange={(e) => setEditState((s) => ({ ...s, lastName: e.target.value }))} />
                </label>
              </div>
              <div className="form-grid">
                <label>
                  Email
                  <input type="email" value={editState.email} onChange={(e) => setEditState((s) => ({ ...s, email: e.target.value }))} />
                </label>
                <label>
                  Phone
                  <input value={editState.phone} onChange={(e) => setEditState((s) => ({ ...s, phone: e.target.value }))} />
                </label>
              </div>
              <label>
                Company
                <input value={editState.company} onChange={(e) => setEditState((s) => ({ ...s, company: e.target.value }))} />
              </label>
              <label>
                Tags (comma separated)
                <input value={editState.tags} onChange={(e) => setEditState((s) => ({ ...s, tags: e.target.value }))} />
              </label>
              <div className="form-actions">
                <button className="btn" type="button" onClick={closeEditor}>
                  Cancel
                </button>
                <button className="btn btn--primary" type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
